/**
 * SEO de "Consultas resueltas": rutas, slugs de sector y metadatos.
 *
 * De cara al usuario el bloque se presenta como "Lo que han preguntado otros
 * negocios como el tuyo"; el nombre técnico y la URL son "consultas resueltas"
 * (`/consultas-resueltas`, en inglés `/en/answers`).
 */
import {
  CATEGORY_MODULE_KEYS,
  PHASE_SECTORS,
  SECTOR_LABELS,
  QUESTION_BANK,
  SECTOR_QUESTIONS,
  questionBySlug,
  questionSlug,
  questionText,
  type BankQuestion,
  type QuestionCategory,
} from "@/data/questionBank";

export const slugify = (v: string): string =>
  v
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export interface AnswerSector {
  key: string;
  label: string;
  labelEn: string;
  slugEs: string;
  slugEn: string;
}

/** Sectores publicados, en el orden de la fase actual. */
export const ANSWER_SECTORS: AnswerSector[] = PHASE_SECTORS.map((key) => {
  const label = SECTOR_LABELS[key]?.es ?? key;
  const labelEn = SECTOR_LABELS[key]?.en ?? key;
  return { key, label, labelEn, slugEs: slugify(label), slugEn: slugify(labelEn) };
});

export const sectorLabel = (sector: AnswerSector, lang: "es" | "en") =>
  lang === "es" ? sector.label : sector.labelEn;

export const sectorSlug = (sector: AnswerSector, lang: "es" | "en") =>
  lang === "es" ? sector.slugEs : sector.slugEn;

export const sectorFromKey = (key: string): AnswerSector | null =>
  ANSWER_SECTORS.find((s) => s.key === key) ?? null;

export const sectorFromSlug = (slug: string, lang: "es" | "en"): AnswerSector | null => {
  const s = (slug || "").toLowerCase();
  return ANSWER_SECTORS.find((x) => (lang === "es" ? x.slugEs : x.slugEn) === s) ?? null;
};

export const answersBase = (lang: "es" | "en") =>
  lang === "es" ? "/consultas-resueltas" : "/en/answers";

export const answerPath = (lang: "es" | "en", sector: AnswerSector, question: BankQuestion) =>
  `${answersBase(lang)}/${sectorSlug(sector, lang)}/${questionSlug(question, lang)}`;

/** Preguntas publicadas de un sector (objetos del banco, en orden). */
export const sectorAnswerQuestions = (sectorKey: string): BankQuestion[] =>
  (SECTOR_QUESTIONS[sectorKey] ?? [])
    .map((id) => QUESTION_BANK.find((x) => x.id === id))
    .filter((x): x is BankQuestion => !!x);

/** Otros sectores que tienen publicada la misma pregunta. */
export const sectorsWithQuestion = (questionId: string, exceptKey?: string): AnswerSector[] =>
  ANSWER_SECTORS.filter(
    (s) => s.key !== exceptKey && (SECTOR_QUESTIONS[s.key] ?? []).includes(questionId),
  );

export const resolveAnswerParams = (
  sectorSlugParam: string,
  questionSlugParam: string,
  lang: "es" | "en",
): { sector: AnswerSector; question: BankQuestion } | null => {
  const sector = sectorFromSlug(sectorSlugParam, lang);
  const question = questionBySlug(questionSlugParam, lang);
  if (!sector || !question) return null;
  if (!(SECTOR_QUESTIONS[sector.key] ?? []).includes(question.id)) return null;
  return { sector, question };
};

// ── Metadatos ────────────────────────────────────────────────────────────

export const answerHead = (
  lang: "es" | "en",
  sectorName: string,
  questionLabel: string,
  category?: QuestionCategory,
): { title: string; description: string } =>
  buildAnswerHead(lang, sectorName, questionLabel, category);

const CATEGORY_SEARCH_INTENT: Record<QuestionCategory, { es: string; en: string }> = {
  clientes: { es: "clientes y ventas", en: "customers and sales" },
  finanzas: { es: "precios, costes y rentabilidad", en: "pricing, costs and profitability" },
  equipo: { es: "equipo y personal", en: "team and staff" },
  gestion: { es: "gestión diaria", en: "day-to-day management" },
  marketing: { es: "marketing local y visibilidad", en: "local marketing and visibility" },
  digital: { es: "digitalización y tecnología", en: "digital tools and technology" },
  legal: { es: "gestión legal, fiscal y administrativa", en: "legal, tax and administrative management" },
  crecimiento: { es: "crecimiento rentable", en: "profitable growth" },
};

const trimSeoText = (value: string, maximum: number) => {
  if (value.length <= maximum) return value;
  const shortened = value.slice(0, maximum - 1);
  const boundary = shortened.lastIndexOf(" ");
  return `${shortened.slice(0, boundary > maximum * 0.65 ? boundary : maximum - 1).replace(/[,:;.!?]+$/, "")}…`;
};

const buildAnswerHead = (
  lang: "es" | "en",
  sectorName: string,
  questionLabel: string,
  category?: QuestionCategory,
) => {
  const cleanQuestion = questionLabel.replace(/^¿/, "").replace(/\?$/, "");
  const title = trimSeoText(`${sectorName}: ${cleanQuestion} | Retail Lemon`, 65);
  const intent = category ? CATEGORY_SEARCH_INTENT[category][lang] : lang === "es" ? "gestión del negocio" : "business management";
  const description = lang === "es"
    ? `Respuesta práctica para ${sectorName.toLowerCase()} sobre ${intent}. Pasos claros, ejemplos del sector y decisiones aplicables al negocio.`
    : `Practical guidance for ${sectorName.toLowerCase()} owners on ${intent}. Clear steps, industry examples and decisions you can apply.`;
  return { title, description: trimSeoText(description, 158) };
};

/**
 * Descripción única por página: usa el arranque real de la respuesta publicada
 * (texto plano, sin títulos ni viñetas). Si no hay cuerpo, cae en la
 * descripción por categoría, que es genérica y se repetiría entre páginas.
 */
export const answerDescriptionFromBody = (
  body: string | null | undefined,
  fallback: string,
): string => {
  const plain = String(body ?? "")
    .split("\n")
    .map((line) => line.replace(/^#+\s*/, "").replace(/^[-•*]\s*/, "").trim())
    .filter((line) => line.length > 40)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  if (plain.length < 80) return fallback;
  return trimSeoText(plain, 158);
};

/** Fragmento de respuesta usado en el JSON-LD (acceptedAnswer). */
export const answerSchemaText = (body: string | null | undefined, fallback: string): string => {
  const plain = String(body ?? "")
    .split("\n")
    .map((line) => line.replace(/^#+\s*/, "").trim())
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length > 80 ? trimSeoText(plain, 480) : fallback;
};

export const answersIndexHead = (lang: "es" | "en") =>
  lang === "es"
    ? {
        title: "Consultas resueltas por sector | Retail Lemon",
        description:
          "Lo que han preguntado otros negocios como el tuyo: respuestas por sector sobre clientes, dinero, equipo, gestión y marketing.",
      }
    : {
        title: "Answers by business type | Retail Lemon",
        description:
          "What other businesses like yours have asked: answers by sector on customers, money, team, operations and marketing.",
      };

export const sectorIntroText = (lang: "es" | "en", sectorName: string) =>
  lang === "es"
    ? `Preguntas reales de ${sectorName.toLowerCase()} y qué hacer con ellas.`
    : `Real questions from ${sectorName.toLowerCase()} owners and what to do about them.`;

/** Enlace al Agente Retail con sector y pregunta ya rellenados. */
export const agentPathWithQuestion = (
  lang: "es" | "en",
  sectorKey: string,
  question: BankQuestion,
) => {
  const base = lang === "es" ? "/agente-retail-ia" : "/en/retail-ai-agent";
  const params = new URLSearchParams({
    sector: sectorKey,
    pregunta: questionText(question, lang, sectorKey),
    asunto: CATEGORY_MODULE_KEYS[question.category],
    origen: "consulta-resuelta",
  });
  return `${base}?${params.toString()}`;
};
